import React, { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, 
  Users, 
  Database,
  Activity,
  Settings,
  MapPin
} from 'lucide-react';
import * as superAdminAPI from '@/services/superAdminAPI';
import { SectionHeader, LoadingSkeleton, ErrorAlert } from '@/components/admin/common/DashboardComponents';
import { UserStatistics, UserRolesBreakdown } from '@/components/admin/dashboard/UserStatistics';
import { CampaignStatistics, CampaignStatusBreakdown, CampaignTypeBreakdown } from '@/components/admin/dashboard/CampaignStatistics';
import { RecentActivities } from '@/components/admin/dashboard/RecentActivities';
import { DatabaseStatistics, DatabaseTablesInfo } from '@/components/admin/dashboard/DatabaseStatistics';
import { DataIngestionSection } from '@/components/admin/dashboard/DataIngestionSection';
import { UserManagementTable } from '@/components/admin/dashboard/UserManagementTable';
import { POCManagement } from '@/components/admin/poc-management/POCManagement';

function toErrorMessage(err, fallback = 'An unexpected error occurred') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (err instanceof Error && err.message) return err.message;
  if (typeof err.message === 'string') return err.message;
  return fallback;
}

/**
 * Super Admin Dashboard Main Page
 */
export function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dashboardData, setDashboardData] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [databaseStats, setDatabaseStats] = useState(null);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const results = await Promise.allSettled([
        superAdminAPI.fetchSuperAdminDashboard(),
        superAdminAPI.fetchUserStatistics(),
        superAdminAPI.fetchCampaignStatistics(),
        superAdminAPI.fetchRecentActivities(10),
        superAdminAPI.fetchDatabaseStatistics(),
      ]);

      const [dashboard, userStatsData, campaignStatsData, activitiesData, databaseStatsData] = results;

      if (dashboard.status === 'fulfilled') setDashboardData(dashboard.value);
      if (userStatsData.status === 'fulfilled') setUserStats(userStatsData.value);
      if (campaignStatsData.status === 'fulfilled') setCampaignStats(campaignStatsData.value);
      if (activitiesData.status === 'fulfilled') setActivities(activitiesData.value || []);
      if (databaseStatsData.status === 'fulfilled') setDatabaseStats(databaseStatsData.value);

      const failures = results
        .filter((result) => result.status === 'rejected')
        .map((result) => toErrorMessage(result.reason));

      if (failures.length === results.length) {
        setError(failures[0] || 'Failed to load dashboard data');
      } else if (failures.length > 0) {
        setError(`Some sections failed to load: ${failures[0]}`);
      }
    } catch (err) {
      setError(toErrorMessage(err, 'Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'pocs', label: 'POC Management', icon: MapPin },
    { id: 'database', label: 'Database', icon: Database },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Super Admin Dashboard</h1>
          <p className="text-gray-600">
            Manage users, monitor campaigns, and oversee system health
          </p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorAlert
              message={error}
              onRetry={loadDashboardData}
              onDismiss={() => setError(null)}
            />
          </div>
        )}

        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-6 flex justify-end">
          <button
            onClick={loadDashboardData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
        </div>

        {loading && !dashboardData && !userStats ? (
          <LoadingSkeleton type="card" count={4} />
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <SectionHeader title="User Statistics" />
                  <UserStatistics statistics={userStats} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <SectionHeader title="User Roles Distribution" />
                    <UserRolesBreakdown usersByRole={userStats?.users_by_role} />
                  </div>
                  <div>
                    <SectionHeader title="Recent Activities" />
                    <RecentActivities activities={activities} limit={5} />
                  </div>
                </div>

                <div>
                  <SectionHeader title="Campaign Statistics" />
                  <CampaignStatistics statistics={campaignStats} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <SectionHeader title="Campaign Status" />
                    <CampaignStatusBreakdown campaignsByStatus={campaignStats?.campaigns_by_status} />
                  </div>
                  <div>
                    <SectionHeader title="Campaign Types" />
                    <CampaignTypeBreakdown campaignsByType={campaignStats?.campaigns_by_type} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-6">
                <UserManagementTable />
              </div>
            )}

            {activeTab === 'pocs' && (
              <div className="space-y-6">
                <POCManagement />
              </div>
            )}

            {activeTab === 'database' && (
              <div className="space-y-6">
                <div>
                  <SectionHeader title="Data Ingestion" />
                  <DataIngestionSection />
                </div>
                <div>
                  <SectionHeader title="Database Overview" />
                  <DatabaseStatistics statistics={databaseStats} />
                </div>
                <div>
                  <SectionHeader title="Database Tables" />
                  <DatabaseTablesInfo tablesInfo={databaseStats?.tables_info} />
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Settings Coming Soon
                  </h3>
                  <p className="text-gray-600">
                    System settings and configuration options will be available here
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SuperAdminDashboard;
